/**
 * Centralized API client for making HTTP requests to the backend API.
 * Handles authentication, error handling, offline queue management, and request/response interceptors.
 */

import { db } from '../services/offlineStorage';

/**
 * Base API URL - should be configured via environment variables in production
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

/**
 * Request configuration interface
 */
interface RequestConfig extends RequestInit {
  skipAuth?: boolean;
  skipOfflineQueue?: boolean;
  retryOnFailure?: boolean;
}

/**
 * API Error class for structured error handling
 */
export class ApiError extends Error {
  status: number;
  data?: unknown;

  constructor(
    message: string,
    status: number,
    data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Get stored JWT token from localStorage
 */
const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('auth_token');
  } catch (error) {
    console.error('Failed to retrieve auth token:', error);
    return null;
  }
};

/**
 * Set JWT token in localStorage
 */
export const setAuthToken = (token: string): void => {
  try {
    localStorage.setItem('auth_token', token);
  } catch (error) {
    console.error('Failed to store auth token:', error);
  }
};

/**
 * Remove JWT token from localStorage
 */
export const removeAuthToken = (): void => {
  try {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_role');
    localStorage.removeItem('user_data');
  } catch (error) {
    console.error('Failed to remove auth token:', error);
  }
};

/**
 * Check if the application is currently online
 */
const isOnline = (): boolean => {
  return navigator.onLine;
};

/**
 * Build full URL from endpoint path
 */
const buildUrl = (endpoint: string): string => {
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${API_BASE_URL}/${cleanEndpoint}`;
};

/**
 * Request interceptor - adds authentication headers and handles pre-request logic
 */
const requestInterceptor = (
  endpoint: string,
  config: RequestConfig = {}
): [string, RequestInit] => {
  const headers = new Headers(config.headers);

  // Add Content-Type if not already set and body is present
  if (config.body && !headers.has('Content-Type')) {
    if (typeof config.body === 'string') {
      headers.set('Content-Type', 'application/json');
    }
  }

  // Add Authorization header if not skipping auth
  if (!config.skipAuth) {
    const token = getAuthToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  // Add custom headers for tracking
  headers.set('X-Client-Version', '1.0.0');
  headers.set('X-Client-Type', 'PWA');

  const url = buildUrl(endpoint);
  const requestConfig: RequestInit = {
    ...config,
    headers,
  };

  return [url, requestConfig];
};

/**
 * Response interceptor - handles response parsing and error handling
 */
const responseInterceptor = async (response: Response): Promise<unknown> => {
  // Handle 204 No Content
  if (response.status === 204) {
    return null;
  }

  // Parse response body
  const contentType = response.headers.get('Content-Type');
  let data: unknown;

  if (contentType?.includes('application/json')) {
    try {
      data = await response.json();
    } catch (error) {
      console.error('Failed to parse JSON response:', error);
      data = null;
    }
  } else {
    data = await response.text();
  }

  // Handle error responses
  if (!response.ok) {
    // Handle 401 Unauthorized - clear auth and redirect to login
    if (response.status === 401) {
      removeAuthToken();
      window.location.href = '/login';
    }

    const errorMessage =
      typeof data === 'object' && data !== null && 'detail' in data
        ? String((data as { detail: unknown }).detail)
        : `Request failed with status ${response.status}`;

    throw new ApiError(errorMessage, response.status, data);
  }

  return data;
};

/**
 * Queue an action to be synced when online
 */
const queueAction = async (action: {
  endpoint: string;
  method: string;
  data?: unknown;
}): Promise<void> => {
  try {
    await db.pendingActions.add({
      id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'api_request',
      endpoint: action.endpoint,
      method: action.method as 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
      data: action.data,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending',
    });
    console.log('Action queued for sync:', action.endpoint);
  } catch (error) {
    console.error('Failed to queue action:', error);
  }
};

/**
 * Cache response data in IndexedDB
 */
const cacheResponseData = async (endpoint: string, data: unknown): Promise<void> => {
  try {
    // Determine what type of data this is and cache accordingly
    if (endpoint.includes('/activities') && Array.isArray(data)) {
      const { cacheActivities } = await import('../services/offlineStorage');
      await cacheActivities(data as any);
    } else if (endpoint.includes('/workouts') && Array.isArray(data)) {
      const { cacheWorkouts } = await import('../services/offlineStorage');
      await cacheWorkouts(data as any);
    } else if (endpoint.includes('/courses') && Array.isArray(data)) {
      const { cacheCourses } = await import('../services/offlineStorage');
      await cacheCourses(data as any);
    }
  } catch (error) {
    console.error('Failed to cache response data:', error);
  }
};

/**
 * Get cached response data from IndexedDB
 */
const getCachedResponseData = async (endpoint: string): Promise<unknown | null> => {
  try {
    // Determine what type of data to retrieve based on endpoint
    if (endpoint.includes('/activities')) {
      const { getCachedActivities } = await import('../services/offlineStorage');
      return await getCachedActivities();
    } else if (endpoint.includes('/workouts')) {
      const { getCachedWorkouts } = await import('../services/offlineStorage');
      return await getCachedWorkouts();
    } else if (endpoint.includes('/courses')) {
      const { getCachedCourses } = await import('../services/offlineStorage');
      return await getCachedCourses();
    }
    return null;
  } catch (error) {
    console.error('Failed to get cached response data:', error);
    return null;
  }
};

/**
 * Main API request function with offline queue support
 */
const apiRequest = async <T = unknown>(
  endpoint: string,
  config: RequestConfig = {}
): Promise<T> => {
  // Check if online
  if (!isOnline() && !config.skipOfflineQueue) {
    // Queue the request for later if it's a mutation (POST, PUT, PATCH, DELETE)
    const method = config.method?.toUpperCase() || 'GET';
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      await queueAction({
        endpoint,
        method,
        data: config.body ? JSON.parse(config.body as string) : undefined,
      });

      throw new ApiError(
        'You are offline. This action has been queued and will be processed when you reconnect.',
        0
      );
    }

    // For GET requests, try to retrieve from offline storage
    const cachedData = await getCachedResponseData(endpoint);
    if (cachedData) {
      return cachedData as T;
    }

    throw new ApiError('You are offline and no cached data is available.', 0);
  }

  // Prepare request with interceptor
  const [url, requestConfig] = requestInterceptor(endpoint, config);

  try {
    // Make the request
    const response = await fetch(url, requestConfig);

    // Handle response with interceptor
    const data = await responseInterceptor(response);

    // Cache successful GET requests for offline use
    if ((config.method?.toUpperCase() === 'GET' || !config.method) && data) {
      await cacheResponseData(endpoint, data);
    }

    return data as T;
  } catch (error) {
    // Handle network errors
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle fetch errors (network issues, CORS, etc.)
    if (error instanceof TypeError) {
      throw new ApiError(
        'Network error. Please check your connection and try again.',
        0
      );
    }

    // Re-throw unknown errors
    throw error;
  }
};

/**
 * Convenience methods for common HTTP verbs
 */
export const apiClient = {
  /**
   * GET request
   */
  get: <T = unknown>(endpoint: string, config?: RequestConfig): Promise<T> => {
    return apiRequest<T>(endpoint, { ...config, method: 'GET' });
  },

  /**
   * POST request
   */
  post: <T = unknown>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig
  ): Promise<T> => {
    return apiRequest<T>(endpoint, {
      ...config,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * PUT request
   */
  put: <T = unknown>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig
  ): Promise<T> => {
    return apiRequest<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * PATCH request
   */
  patch: <T = unknown>(
    endpoint: string,
    body?: unknown,
    config?: RequestConfig
  ): Promise<T> => {
    return apiRequest<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * DELETE request
   */
  delete: <T = unknown>(endpoint: string, config?: RequestConfig): Promise<T> => {
    return apiRequest<T>(endpoint, { ...config, method: 'DELETE' });
  },

  /**
   * Upload file with multipart/form-data
   */
  upload: <T = unknown>(
    endpoint: string,
    formData: FormData,
    config?: RequestConfig
  ): Promise<T> => {
    // Don't set Content-Type header - browser will set it with boundary
    const headers = new Headers(config?.headers);
    headers.delete('Content-Type');

    return apiRequest<T>(endpoint, {
      ...config,
      method: 'POST',
      body: formData,
      headers,
    });
  },
};

/**
 * Process pending actions when online
 */
export const processPendingActions = async (): Promise<void> => {
  if (!navigator.onLine) {
    console.log('Cannot process pending actions while offline');
    return;
  }

  try {
    const pendingActions = await db.pendingActions
      .where('status')
      .equals('pending')
      .toArray();

    console.log(`Processing ${pendingActions.length} pending actions`);

    for (const action of pendingActions) {
      try {
        // Update status to processing
        await db.pendingActions.update(action.id, { status: 'processing' });

        // Execute the action
        await apiRequest(action.endpoint, {
          method: action.method,
          body: action.data ? JSON.stringify(action.data) : undefined,
          skipOfflineQueue: true,
        });

        // Mark as completed
        await db.pendingActions.update(action.id, { status: 'completed' });
        
        // Delete completed action after a delay
        setTimeout(() => {
          db.pendingActions.delete(action.id).catch(console.error);
        }, 5000);

        console.log(`Successfully synced action: ${action.endpoint}`);
      } catch (error) {
        console.error(`Failed to sync action ${action.endpoint}:`, error);

        // Update retry count
        const retryCount = action.retryCount + 1;

        if (retryCount >= 3) {
          // Mark as failed after 3 retries
          await db.pendingActions.update(action.id, { 
            status: 'failed',
            retryCount 
          });
        } else {
          // Reset to pending for retry
          await db.pendingActions.update(action.id, { 
            status: 'pending',
            retryCount 
          });
        }
      }
    }
  } catch (error) {
    console.error('Error processing pending actions:', error);
  }
};

/**
 * Set up automatic sync when connection is restored
 */
export const setupAutoSync = (): void => {
  window.addEventListener('online', () => {
    console.log('Connection restored, processing pending actions...');
    processPendingActions().catch(console.error);
  });
};

/**
 * Export default client
 */
export default apiClient;